/**
 * Record Payment Modal
 *
 * Self-contained modal for recording a payment against a single invoice.
 * Used from the Invoice detail page (and optionally the Entity detail page).
 * Includes bank account selection, exchange rate, dual bank fees, JE preview,
 * and advance auto-detection.
 */

import { useState, useEffect } from 'react'

import { useBaseCurrency } from '../../hooks/use-currencies'
import { fromSmallestUnit } from '../../utils/formatters'
import { Modal } from '../ui'
import { RecordPaymentAdvance } from './RecordPaymentAdvance'
import { RecordPaymentFees } from './RecordPaymentFees'
import { RecordPaymentFormFields } from './RecordPaymentFormFields'
import { RecordPaymentFullyPaid } from './RecordPaymentFullyPaid'
import { RecordPaymentPreview } from './RecordPaymentPreview'
import { RecordPaymentSummaryBanner } from './RecordPaymentSummaryBanner'
import { usePaymentFormData } from './usePaymentFormData'
import { useFormReset, useComputedPaymentState } from './usePaymentFormState'
import { useRecordPaymentSubmit } from './useRecordPaymentSubmit'
import { useFiscalPeriodCheck } from '../../hooks/useFiscalPeriodCheck'
import { FiscalPeriodWarning } from '../FiscalPeriodWarning'

import type { Invoice } from '../../types/billing'

// ============================================================================
// Types
// ============================================================================

export interface RecordPaymentModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  organizationId: string
  ledgerId: string
  invoice: Invoice
}

// ============================================================================
// Helpers
// ============================================================================

function deriveInvoiceInfo(invoice: Invoice, orgBaseCurrency: string): {
  invoiceCurrency: string; baseCurrency: string; isForeignCurrency: boolean
  outstandingBalance: number; isVendorBill: boolean; totalAmount: number; paymentsReceived: number
} {
  const invoiceCurrency = invoice.currency ?? orgBaseCurrency
  const baseCurrency = invoice.baseCurrency ?? orgBaseCurrency
  const totalAmount = invoice.totalAmount ?? 0
  const paymentsReceived = invoice.paymentsReceived ?? 0
  return {
    invoiceCurrency, baseCurrency,
    isForeignCurrency: invoiceCurrency !== baseCurrency,
    outstandingBalance: fromSmallestUnit(totalAmount - paymentsReceived, invoiceCurrency),
    isVendorBill: invoice.invoiceType === 'vendor_bill' || invoice.invoiceType === 'vendor_credit_note',
    totalAmount, paymentsReceived,
  }
}

interface PaymentFormState {
  paymentDate: Date
  setPaymentDate: React.Dispatch<React.SetStateAction<Date>>
  paymentAmount: number | null
  setPaymentAmount: React.Dispatch<React.SetStateAction<number | null>>
  paymentMethod: string
  setPaymentMethod: React.Dispatch<React.SetStateAction<string>>
  paymentRef: string
  setPaymentRef: React.Dispatch<React.SetStateAction<string>>
  paymentNotes: string
  setPaymentNotes: React.Dispatch<React.SetStateAction<string>>
  selectedBankAccountId: string
  setSelectedBankAccountId: React.Dispatch<React.SetStateAction<string>>
  intermediaryFee: string
  setIntermediaryFee: React.Dispatch<React.SetStateAction<string>>
  hasSeparateIntermediaryFee: boolean
  setSeparateIntermediaryFee: React.Dispatch<React.SetStateAction<boolean>>
  destinationFee: string
  setDestinationFee: React.Dispatch<React.SetStateAction<string>>
  hasSeparateDestinationFee: boolean
  setSeparateDestinationFee: React.Dispatch<React.SetStateAction<boolean>>
  tdsAmount: string
  setTdsAmount: React.Dispatch<React.SetStateAction<string>>
  shouldWriteOffRemaining: boolean
  setWriteOffRemaining: React.Dispatch<React.SetStateAction<boolean>>
}

function usePaymentFormState(): PaymentFormState {
  const [paymentDate, setPaymentDate] = useState<Date>(new Date())
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null)
  const [paymentMethod, setPaymentMethod] = useState('PAYMENT_METHOD_BANK_TRANSFER')
  const [paymentRef, setPaymentRef] = useState('')
  const [paymentNotes, setPaymentNotes] = useState('')
  const [selectedBankAccountId, setSelectedBankAccountId] = useState('')
  const [intermediaryFee, setIntermediaryFee] = useState('')
  const [hasSeparateIntermediaryFee, setSeparateIntermediaryFee] = useState(true)
  const [destinationFee, setDestinationFee] = useState('')
  const [hasSeparateDestinationFee, setSeparateDestinationFee] = useState(true)
  const [tdsAmount, setTdsAmount] = useState('')
  const [shouldWriteOffRemaining, setWriteOffRemaining] = useState(false)
  return {
    paymentDate, setPaymentDate,
    paymentAmount, setPaymentAmount,
    paymentMethod, setPaymentMethod,
    paymentRef, setPaymentRef,
    paymentNotes, setPaymentNotes,
    selectedBankAccountId, setSelectedBankAccountId,
    intermediaryFee, setIntermediaryFee,
    hasSeparateIntermediaryFee, setSeparateIntermediaryFee,
    destinationFee, setDestinationFee,
    hasSeparateDestinationFee, setSeparateDestinationFee,
    tdsAmount, setTdsAmount,
    shouldWriteOffRemaining, setWriteOffRemaining,
  }
}

// ============================================================================
// Sub-component: body shown when there is an outstanding balance
// ============================================================================

interface PaymentBodyProps {
  formData: ReturnType<typeof usePaymentFormData>
  computed: ReturnType<typeof useComputedPaymentState>
  state: ReturnType<typeof usePaymentFormState>
  isSaving: boolean
  handleSubmit: () => void
  invoiceCurrency: string
  baseCurrency: string
  isForeignCurrency: boolean
  outstandingBalance: number
  onClose: () => void
}

function PaymentBody(props: PaymentBodyProps): JSX.Element {
  const { formData, state, invoiceCurrency, outstandingBalance } = props
  return (
    <>
      <RecordPaymentAdvance
        advanceAccountCode={formData.advanceAccountCode}
        advanceApplied={formData.advanceApplied}
        advanceBalance={formData.advanceBalance}
        invoiceCurrency={invoiceCurrency}
        outstandingBalance={outstandingBalance}
        paymentAmount={state.paymentAmount}
        setAdvanceApplied={formData.setAdvanceApplied}
        setPaymentAmount={state.setPaymentAmount}
      />
      {formData.isDataLoading
        ? <div className="el-py-xl text-center text-foreground-secondary text-sm">Loading payment data...</div>
        : <PaymentBodyDetails {...props} />}
    </>
  )
}

function PaymentBodyDetails(props: PaymentBodyProps): JSX.Element {
  const { formData, computed, state, isSaving, handleSubmit, invoiceCurrency, baseCurrency, isForeignCurrency, onClose } = props
  return (
    <>
      <RecordPaymentFormFields
        baseCurrency={baseCurrency} invoiceCurrency={invoiceCurrency}
        isExchangeRateLoading={formData.isExchangeRateLoading} isForeignCurrency={isForeignCurrency}
        paymentAccountOptions={computed.paymentAccountOptions} paymentAmount={state.paymentAmount}
        paymentDate={state.paymentDate} paymentExchangeRate={formData.paymentExchangeRate}
        paymentMethod={state.paymentMethod} paymentNotes={state.paymentNotes}
        paymentRef={state.paymentRef} selectedBankAccountId={state.selectedBankAccountId}
        setPaymentAmount={state.setPaymentAmount} setPaymentDate={state.setPaymentDate}
        setPaymentExchangeRate={formData.setPaymentExchangeRate} setPaymentMethod={state.setPaymentMethod}
        setPaymentNotes={state.setPaymentNotes} setPaymentRef={state.setPaymentRef}
        setSelectedBankAccountId={state.setSelectedBankAccountId}
      />
      <RecordPaymentFees
        baseCurrency={baseCurrency} destinationFee={state.destinationFee}
        feeAccountId={formData.feeAccountId} feeAccountOptions={computed.feeAccountOptions}
        hasSeparateDestinationFee={state.hasSeparateDestinationFee}
        hasSeparateIntermediaryFee={state.hasSeparateIntermediaryFee}
        intermediaryFee={state.intermediaryFee} invoiceCurrency={invoiceCurrency}
        setDestinationFee={state.setDestinationFee} setFeeAccountId={formData.setFeeAccountId}
        setIntermediaryFee={state.setIntermediaryFee}
        setSeparateDestinationFee={state.setSeparateDestinationFee}
        setSeparateIntermediaryFee={state.setSeparateIntermediaryFee}
        setTdsAccountId={formData.setTdsAccountId} setTdsAmount={state.setTdsAmount}
        tdsAccountId={formData.tdsAccountId} tdsAccountOptions={computed.tdsAccountOptions}
        tdsAmount={state.tdsAmount}
      />
      <RecordPaymentPreview
        advanceApplied={formData.advanceApplied} baseCurrency={baseCurrency}
        invoiceCurrency={invoiceCurrency} isSaving={isSaving}
        paymentAmount={state.paymentAmount} previewBalance={computed.previewBalance}
        previewLines={computed.previewLines} remainingAfterPayment={computed.remainingAfterPayment}
        shouldShowWriteOff={computed.isWriteOffVisible}
        shouldWriteOffRemaining={state.shouldWriteOffRemaining}
        unallocatedAmount={computed.unallocatedAmount}
        onClose={onClose} onSubmit={handleSubmit}
        onWriteOffRemainingChange={state.setWriteOffRemaining}
      />
    </>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export function RecordPaymentModal({
  isOpen, onClose, onSuccess, organizationId, ledgerId, invoice,
}: RecordPaymentModalProps): JSX.Element {
  const orgBaseCurrency = useBaseCurrency()
  const invoiceInfo = deriveInvoiceInfo(invoice, orgBaseCurrency)
  const { invoiceCurrency, baseCurrency, isForeignCurrency, outstandingBalance, isVendorBill, totalAmount, paymentsReceived } = invoiceInfo

  const state = usePaymentFormState()
  const formData = usePaymentFormData({
    isOpen, organizationId, ledgerId, partyId: invoice.partyId,
    invoiceType: invoice.invoiceType, isForeignCurrency, invoiceCurrency, baseCurrency, outstandingBalance,
  })
  useRecordPaymentEffects(isOpen, state, formData, outstandingBalance)

  const computed = useComputedPaymentState({
    paymentMethod: state.paymentMethod, paymentAmount: state.paymentAmount,
    formData, outstandingBalance, isForeignCurrency, invoiceCurrency, baseCurrency,
    invoiceExchangeRate: invoice.exchangeRate || '0',
    intermediaryFee: state.intermediaryFee, destinationFee: state.destinationFee,
    hasSeparateIntermediaryFee: state.hasSeparateIntermediaryFee,
    selectedBankAccountId: state.selectedBankAccountId, shouldWriteOffRemaining: state.shouldWriteOffRemaining,
    tdsAmount: state.tdsAmount, invoiceType: invoice.invoiceType,
    partyName: invoice.partyName, isVendorBill,
  })
  useDefaultBankAccountSync(state, computed)

  const { warning: periodWarning } = useFiscalPeriodCheck(state.paymentDate, organizationId)

  const { isSaving, handleSubmit } = usePaymentSubmit({
    invoice, state, formData, computed, invoiceInfo,
    organizationId, ledgerId, onSuccess, onClose,
  })

  return (
    <Modal closeOnEscape={!isSaving} closeOnOutsideClick={!isSaving} isOpen={isOpen} size="3xl" title={`Record Payment — ${invoice.invoiceNumber}`} onClose={onClose}>
      <div className="el-p-md space-y-4">
        <RecordPaymentSummaryBanner invoiceCurrency={invoiceCurrency} invoiceNumber={invoice.invoiceNumber} partyName={invoice.partyName} paymentsReceived={paymentsReceived} totalAmount={totalAmount} />
        <FiscalPeriodWarning message={periodWarning} />
        {outstandingBalance <= 0 ? (
          <RecordPaymentFullyPaid invoiceCurrency={invoiceCurrency} paymentsReceived={paymentsReceived} totalAmount={totalAmount} onClose={onClose} />
        ) : (
          <PaymentBody baseCurrency={baseCurrency} computed={computed} formData={formData} handleSubmit={handleSubmit} invoiceCurrency={invoiceCurrency} isForeignCurrency={isForeignCurrency} isSaving={isSaving} outstandingBalance={outstandingBalance} state={state} onClose={onClose} />
        )}
      </div>
    </Modal>
  )
}

interface PaymentSubmitArgs {
  invoice: Invoice
  state: ReturnType<typeof usePaymentFormState>
  formData: ReturnType<typeof usePaymentFormData>
  computed: ReturnType<typeof useComputedPaymentState>
  invoiceInfo: ReturnType<typeof deriveInvoiceInfo>
  organizationId: string
  ledgerId: string
  onSuccess: () => void
  onClose: () => void
}

function usePaymentSubmit({
  invoice, state, formData, computed, invoiceInfo, organizationId, ledgerId, onSuccess, onClose,
}: PaymentSubmitArgs): ReturnType<typeof useRecordPaymentSubmit> {
  const { invoiceCurrency, baseCurrency, isForeignCurrency, isVendorBill } = invoiceInfo
  return useRecordPaymentSubmit({
    paymentAmount: state.paymentAmount, advanceApplied: formData.advanceApplied,
    isForeignCurrency, paymentExchangeRate: formData.paymentExchangeRate,
    isVendorBill, partyId: invoice.partyId, partyName: invoice.partyName,
    paymentDate: state.paymentDate, paymentMethod: state.paymentMethod,
    paymentRef: state.paymentRef, selectedBankAccountId: state.selectedBankAccountId,
    invoiceCurrency, baseCurrency, invoiceId: invoice.id,
    allocatedAmount: computed.allocatedAmount, paymentNotes: state.paymentNotes,
    intermediaryFee: state.intermediaryFee, hasSeparateIntermediaryFee: state.hasSeparateIntermediaryFee,
    destinationFee: state.destinationFee, hasSeparateDestinationFee: state.hasSeparateDestinationFee,
    feeAccountId: formData.feeAccountId, tdsAmount: state.tdsAmount, tdsAccountId: formData.tdsAccountId,
    shouldWriteOffRemaining: state.shouldWriteOffRemaining, remainingAfterPayment: computed.remainingAfterPayment,
    fxImpact: computed.fxImpact, organizationId, ledgerId, onSuccess, onClose,
  })
}

function useRecordPaymentEffects(
  isOpen: boolean,
  state: ReturnType<typeof usePaymentFormState>,
  formData: ReturnType<typeof usePaymentFormData>,
  outstandingBalance: number,
): void {
  const { setSelectedBankAccountId } = state
  useFormReset(isOpen, outstandingBalance, {
    setPaymentDate: state.setPaymentDate, setPaymentAmount: state.setPaymentAmount,
    setPaymentMethod: state.setPaymentMethod, setPaymentRef: state.setPaymentRef,
    setPaymentNotes: state.setPaymentNotes, setIntermediaryFee: state.setIntermediaryFee,
    setSeparateIntermediaryFee: state.setSeparateIntermediaryFee, setDestinationFee: state.setDestinationFee,
    setSeparateDestinationFee: state.setSeparateDestinationFee, setWriteOffRemaining: state.setWriteOffRemaining,
    setTdsAmount: state.setTdsAmount,
  })

  useEffect(() => {
    if (formData.defaultBankAccountId) { setSelectedBankAccountId(formData.defaultBankAccountId) }
  }, [formData.defaultBankAccountId, setSelectedBankAccountId])
}

function useDefaultBankAccountSync(
  state: ReturnType<typeof usePaymentFormState>,
  computed: ReturnType<typeof useComputedPaymentState>,
): void {
  const { setSelectedBankAccountId } = state
  useEffect(() => {
    if (computed.paymentAccountOptions.length > 0
        && !computed.paymentAccountOptions.find(o => o.value === state.selectedBankAccountId)) {
      setSelectedBankAccountId(computed.paymentAccountOptions[0]?.value ?? '')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computed.paymentAccountOptions])
}

export default RecordPaymentModal
